/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'

import { font } from '../css'
import { FormButton } from '../Form/css'
import { Flex } from '../Layout'
import { media } from '../Responsive'
import { useUrlManager } from 'src/hooks/useUrlManager'
import { SelectInput } from '../ts-components/SelectInput'
import Text from '../ts-components/Text'
import Button from '../ts-components/Button'

export const useRosterSelect = (
  teamId,
  existingRosterIdFromLocationState = ''
) => {
  const { currentQueryParams } = useUrlManager()

  const currentParams = useMemo(() => {
    return {
      sn_schedule: currentQueryParams.get('sn_schedule'),
      sn_division: currentQueryParams.get('sn_division'),
      sn_team: currentQueryParams.get('sn_team'),
      sn_team_name: currentQueryParams.get('sn_team_name'),
      sn_starts_at: currentQueryParams.get('sn_starts_at'),
      sn_ends_at: currentQueryParams.get('sn_ends_at'),
      sn_sort: currentQueryParams.get('sn_sort') || 'asc',
      sn_today: Boolean(currentQueryParams.get('sn_today')),
      sn_sort_by: currentQueryParams.get('sn_sort_by'),
      sn_schedule_presentation: currentQueryParams.get(
        'sn_schedule_presentation'
      ),
    }
  }, [currentQueryParams])

  const { sn_schedule } = currentParams
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [rosters, setRosters] = useState([])
  const [selectedRosterId, setSelectedRosterId] = useState('')
  const [preexistingRosterId, setPreexistingRosterId] = useState(
    sn_schedule || existingRosterIdFromLocationState
  )

  const loadRosters = useCallback(async () => {
    try {
      const data = await req(`/teams/${teamId}/rosters`)

      const mapped = data.map((d) => {
        const { uid, name } = d

        return {
          ...d,
          id: uid,
          name,
        }
      })
      setRosters(mapped)
      setTimeout(() => {
        if (sn_schedule) {
          const roster = mapped.find((r) => r.schedule_uid === sn_schedule)
          if (roster) {
            setSelectedRosterId(roster.id)
          }
        } else if (preexistingRosterId) {
          setSelectedRosterId(existingRosterIdFromLocationState)
          setPreexistingRosterId(null)
        } else {
          if (mapped.length > 0) {
            setSelectedRosterId(mapped[0].id)
          }
        }
      }, 0)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [teamId])

  useEffect(() => {
    loadRosters()
  }, [loadRosters])

  return {
    error,
    loading,
    rosters,
    selectedRosterId,
    setSelectedRosterId,
    loadRosters,
    currentParams,
  }
}

const RosterSelectNew = ({
  teamName,
  teamId,
  permission,
  selectedRosterId,
  setSelectedRosterId,
  rosters,
  loadRosters,
  inFrame,
  setParam = () => {},
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const getSubTitle = (roster) => {
    console.log('roster', roster)
    let subTitle = 'Unlinked Roster'

    if (roster.starts_at) {
      const starts = dayjs(roster.starts_at, 'YYYY-MM-DD hh:mm:ss')
      const ends = dayjs(roster.ends_at, 'YYYY-MM-DD hh:mm:ss')
      subTitle = `${starts.format('MMM D, YYYY')} - ${ends.format(
        'MMM D, YYYY'
      )}`
    }
    if (roster?.organization_name) {
      subTitle = `${roster?.organization_name} • ${subTitle}`
    }
    return subTitle
  }

  // Transform rosters to options format for SelectInput with custom render
  const options = useMemo(() => {
    return rosters.map((roster) => ({
      value: roster.id,
      label: (
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <Text variant='paragraphMedium' color={colors.NEUTRAL_0}>
            {roster?.schedule_name || roster?.name}
          </Text>
          <Text variant='paragraphXSmall' color={colors.NEUTRAL_100}>
            {getSubTitle(roster)}
          </Text>
        </div>
      ),
    }))
  }, [rosters])

  const handleRosterChange = (value) => {
    const selectedRoster = rosters.find((r) => r.id === value)
    if (selectedRoster) {
      setParam('sn_schedule', selectedRoster.schedule_uid)
      setSelectedRosterId(selectedRoster.id)
    }
  }

  const createNewRoster = useCallback(async () => {
    const body = JSON.stringify({
      name: `${teamName} ${dayjs().format('YYYY')}`,
    })
    await req(`/teams/${teamId}/rosters`, {
      method: 'POST',
      body,
    })
    await loadRosters()
  }, [teamId, teamName, loadRosters])

  return (
    <div>
      {rosters.length === 0 ? (
        !inFrame && (permission?.update || permission?.admin) ? (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 16px;
              justify-content: center;
              align-items: center;
            `}
          >
            <Text variant='captionLarge' color={colors.NEUTRAL_0}>
              No Team Rosters Exist!
            </Text>
            <Button
              onClick={createNewRoster}
              variant='primary'
              size='small'
              text='Create A New Team Roster'
            />
          </div>
        ) : (
          // <Flex
          //   column
          //   alignItems='center'
          //   css={css`
          //     padding-bottom: 16px;
          //     margin-bottom: -24px;
          //     border-bottom: 1px solid #3f4753;
          //   `}
          // >
          //   <h2
          //     css={css`
          //       ${font.title}
          //       font-size: 20px;
          //       font-weight: 700;
          //       margin-bottom: 16px;
          //     `}
          //   >
          //     No team rosters exist!
          //   </h2>
          //   <FormButton
          //     busy={isSubmitting}
          //     disabled={isSubmitting}
          //     isSubmit
          //     css={css`
          //       font-size: 14px;
          //       width: 186px;
          //       height: 40px;
          //     `}
          //     onClick={async () => {
          //       const body = JSON.stringify({
          //         name: `${teamName} ${dayjs().format('YYYY')}`,
          //       })
          //       try {
          //         setIsSubmitting(true)
          //         await req(`/teams/${teamId}/rosters`, {
          //           method: 'POST',
          //           body,
          //         })
          //         await loadRosters()
          //       } finally {
          //         setIsSubmitting(false)
          //       }
          //     }}
          //   >
          //     Create a new team roster
          //   </FormButton>
          // </Flex>
          false
        )
      ) : (
        <div
          css={css`
            margin-bottom: 8px;
            width: 400px;

            ${media.mobile} {
              margin-left: 16px;
            }
            display: flex;
            flex-direction: column;
            flex: 1 0 0;
            gap: 8px;
          `}
        >
          <SelectInput
            label='Team Rosters'
            disabled={false}
            options={options}
            name='roster-select'
            value={selectedRosterId}
            onChange={handleRosterChange}
            fieldErrors={[]}
            customError={{}}
            showSearch={false}
            filterOption={(input, option) => {
              const roster = rosters.find((r) => r.id === option.value)
              if (!roster) {
                return false
              }
              const name = (
                roster?.schedule_name ||
                roster?.name ||
                ''
              ).toLowerCase()
              const subtitle = getSubTitle(roster).toLowerCase()
              const searchTerm = input.toLowerCase()
              return name.includes(searchTerm) || subtitle.includes(searchTerm)
            }}
            style={css`
              width: 400px;
              min-height: 50px;
            `}
          />
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    inFrame: state.auth.inFrame,
  }
}

export default connect(mapStateToProps)(RosterSelectNew)
